import React, { useMemo } from 'react';
import { Table, Button } from 'antd';
import {
  IconTypeNames,
  FileFormatNames,
  TableSizeNames,
  SkeletonHOC,
  ButtonShapes,
  CommonTable,
  ButtonTypes,
  PrintButton,
} from 'common';
import { formatDate, formatTime, getAccessToken } from 'common/helpers';
import { COLUMN_NAMES } from './RecentReports.constants';
import { Report } from './models';
import { useDispatch } from 'react-redux';
import { exportReport, RecentReportsDOMType } from '.';
import { useAccount, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
const { Column } = Table;

interface Props {
  recentReports: Report[];
}

const getIconType = (dataIndex: string) => {
  switch (dataIndex) {
    case FileFormatNames.TIFF:
      return IconTypeNames.TIFF;
    case FileFormatNames.PDF:
      return IconTypeNames.PDF;
    case FileFormatNames.HTML:
      return IconTypeNames.HTML;
    case FileFormatNames.DOC:
      return IconTypeNames.WORD;
    default:
      return IconTypeNames.FILE;
  }
};

const RecentReportsTable: React.FC<Props> = ({ recentReports }) => {
  const dispatch = useDispatch();
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});

  const getRender =
    (columnName: string, dispatch: any) =>
    (text: string, record: RecentReportsDOMType) => {
      switch (columnName) {
        case COLUMN_NAMES.fileFormat.dataIndex:
          return (
            <>
              <Button
                className={
                  record.fileFormat === FileFormatNames.HTML ? 'w-50' : 'w-100'
                }
                type={ButtonTypes.LINK}
                shape={ButtonShapes.ROUND}
                size={TableSizeNames.SMALL}
                disabled={
                  record.status === 'pending' ||
                  record.status === 'error' ||
                  record.status === 'unavailable'
                }
                icon={getIconType(text)}
                onClick={() => exportReportClick(record)}
              >
                Download
              </Button>
              {record.fileFormat === FileFormatNames.HTML && (
                <PrintButton
                  className="w-50"
                  type={ButtonTypes.LINK}
                  shape={ButtonShapes.ROUND}
                  size={TableSizeNames.SMALL}
                  disabled={
                    record.status === 'pending' ||
                    record.status === 'error' ||
                    record.status === 'unavailable'
                  }
                  icon={IconTypeNames.PRINTER}
                  onClick={() => exportReportClick(record, true)}
                >
                  Print
                </PrintButton>
              )}
            </>
          );
        default:
          return text;
      }
    };

  const exportReportClick = (record: RecentReportsDOMType, print = false) => {
    if (account && inProgress === InteractionStatus.None) {
      getAccessToken(instance, account, inProgress).then((response) => {
        if (response?.accessToken)
          dispatch(exportReport(record, print, response.accessToken));
      });
    }
  };

  const reports = useMemo(
    () =>
      recentReports.map((report) => ({
        ...report,
        // set status to pending if the record is finished but file storage id is still 0
        status:
          report.status === 'finished' && report.fileStorageId <= 0
            ? 'pending'
            : report.status,
        [COLUMN_NAMES.date.dataIndex]: formatDate(new Date(report.createDate)),
        [COLUMN_NAMES.time.dataIndex]: formatTime(new Date(report.createDate)),
      })),
    [recentReports],
  );

  return (
    <CommonTable items={reports} rowKey={'guid'}>
      {Object.values(COLUMN_NAMES).map(({ title, dataIndex, width }, index) => (
        <Column
          width={width}
          {...{ title, dataIndex, render: getRender(dataIndex, dispatch) }}
          key={index}
        />
      ))}
    </CommonTable>
  );
};

export default SkeletonHOC({ initProp: 'recentReports', rows: 8 })(
  RecentReportsTable,
);
